// AuthLogoutFlow — the `onUnauthorized` target http calls on any protected-call 401, and the
// topbar logout (contract 06 §8). Invoked OUTSIDE any component, so navigation is a
// constructor-injected collaborator (the composition root injects a router-backed navigate),
// not a `@/router` import inside the flow. Logout is best-effort and idempotent: the local
// reset happens even when the server call fails.
import { authService, type AuthService } from '../service/AuthService'
import { useAuthStore, type AuthStore } from '../stores/authStore'
import { publishSessionReset } from '../stores/crossTabReset'
import { runSessionResets } from '../stores/sessionReset'

import { clearAuthAttribution } from '@/features/auth/service/authAttribution'
import { shutdownIntercom as shutdownIntercomWidget } from '@/shared/lib/analytics/intercom'

const LOGIN_ROUTE = 'Login'

export interface AuthLogoutDeps {
  store?: AuthStore
  service?: AuthService
  /** Navigate by route name (composition-root injects a router-backed impl). Default no-op. */
  navigate?: (routeName: string, next?: string) => void
  /** Intercom teardown (v3 `shutdownIntercom`). Defaults to the real widget call — a no-op when
   *  Intercom was never installed (no `INTERCOM_APP_ID`). */
  shutdownIntercom?: () => void
  /** Cross-tab broadcast. Default publishes; the cross-tab subscriber passes a no-op to avoid a loop. */
  publish?: (now: number) => void
  /** Clock injection for testability. */
  now?: () => number
}

export class AuthLogoutFlow {
  private readonly store: AuthStore
  private readonly service: AuthService
  private readonly navigate: (routeName: string, next?: string) => void
  private readonly shutdownIntercom: () => void
  private readonly publish: (now: number) => void
  private readonly now: () => number

  constructor(deps: AuthLogoutDeps = {}) {
    this.store = deps.store ?? useAuthStore()
    this.service = deps.service ?? authService
    this.navigate = deps.navigate ?? ((): void => {})
    this.shutdownIntercom = deps.shutdownIntercom ?? shutdownIntercomWidget
    this.publish = deps.publish ?? publishSessionReset
    this.now = deps.now ?? ((): number => Date.now())
  }

  /** Full logout: best-effort server call + local teardown + cross-tab broadcast. */
  async execute(): Promise<void> {
    const hadToken = this.store.getToken() !== null
    try {
      if (hadToken) await this.service.logout()
    } catch {
      /* best-effort — the reset below runs regardless of a failed/offline logout POST */
    } finally {
      this.resetSession()
      this.publish(this.now())
    }
  }

  /** Idempotent LOCAL teardown (no server call, no re-broadcast). Used by the cross-tab subscriber
   *  too, so a widget identified in this tab is cleared even when another tab drove the logout. */
  resetSession(): void {
    const next = this.currentPathForReturn()
    this.shutdownIntercom()
    clearAuthAttribution()
    runSessionResets()
    this.store.reset()
    if (next) this.navigate(LOGIN_ROUTE, next)
    else this.navigate(LOGIN_ROUTE)
  }

  private currentPathForReturn(): string | null {
    if (typeof window === 'undefined') return null
    const path = `${window.location.pathname}${window.location.search}`
    return path === '/' || path.startsWith('/login') ? null : path
  }
}
